import {Icon} from '@benchling/nucleus-ui';
import {Alert, Divider, Paper} from '@mantine/core';
import React, {useMemo, useState} from 'react';

import {ANALYTICS_MODULE} from 'analytics/AnalyticsTypes';
import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {ChemicalFormatType, EMPTY_MOLFILE} from 'chemistry/ChemistryConstants';
import {
  ChemicalStructureDrawerMode,
  useControlledChemicalStructureDrawer,
} from 'chemistry/marvinjs-wrappers/ChemicalStructureDrawer';
import {MarvinChemicalFormat} from 'chemistry/marvinjs-wrappers/MarvinJSConstants';
import ChemicalCounterpartFields from 'chemistry/molecules/ChemicalCounterpartFields';
import ChemicalStructureModalInput from 'chemistry/molecules/ChemicalStructureModalInput';
import * as MoleculeActions from 'chemistry/molecules/MoleculeActions';
import * as MoleculeDAO from 'chemistry/molecules/MoleculeDAO';
import SchemaMismatchAlert from 'chemistry/molecules/SchemaMismatchAlert';
import useCounterpartDetection from 'chemistry/molecules/useCounterpartDetection';
import useSchemaMismatchDetection, {
  MismatchStatus,
  SelectedSchemaRole,
} from 'chemistry/molecules/useSchemaMismatchDetection';
import type {SchemaMismatchResult} from 'chemistry/molecules/useSchemaMismatchDetection';
import {
  COMPOUND_FIELD_SYSTEM_NAME,
  MoleculeSchemaInterfaceApiId,
} from 'chemistry/schema-configuration/MoleculeSchemaInterfaceConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import FolderDAOCache from 'file-browser/FolderDAOCache';
import FolderSelector, {DEFAULT_PROJECT_FOLDER_LABEL_TEXT} from 'file-browser/FolderSelector';
import FileStates from 'FileStates';
import {SchemaTag} from 'folder-items/FileConstants';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import {writeFragmentsToCache} from 'graphql-client/ApolloWrappers';
import {QueryResultType} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import notifications from 'notifications';
import CreateBioentityTagInputs, {
  useCreateBioentityTagInputs,
} from 'registry/create-bioentity/CreateBioentityTagInputs';
import {
  create as createBioentity,
  CreateBioentityArgs,
  getDefaultCreateBioentityArgs,
} from 'registry/create-bioentity/CreateBioentityUtil';
import TagSchemaSelector from 'schemas/shared/TagSchemaSelector';
import {getLinksKeyFromFieldType, relevantKeyFromFieldType} from 'schemas/tags/TagUtil';
import useSchemaSSIsByIdQuery from 'schemas/util/useSchemaSSIsByIdQuery';
import StudySearchInput, {StudySearchItem} from 'studies/StudySearchInput';
import getTestProps from 'test/lib/cypress/getTestProps';
import Col from 'ui-library/bs/Col';
import FormGroup from 'ui-library/bs/FormGroup';
import Row from 'ui-library/bs/Row';
import {ButtonSize} from 'ui-library/buttons/Button';
import FieldLabel from 'ui-library/inputs/FieldLabel';
import {ExpandableModal} from 'ui-library/modals/ExpandableModal';
import * as net from 'util/Net';
import {withLoadingScreen} from 'util/Promises';

import {stripSaltsAndNotify} from './MoleculeUtil';

const CHEMICAL_FORMAT_TO_MARVIN: Record<ChemicalFormatType, MarvinChemicalFormat> = {
  [ChemicalFormatType.SMILES]: MarvinChemicalFormat.SMILES,
  [ChemicalFormatType.MOLFILE]: MarvinChemicalFormat.MOL,
};

interface CreateMoleculeModalProps {
  initialFolderId: string | null;
  onRequestClose: () => void;
  onRequestSubmit: () => void;
  initialChemicalStructure?: {value: string; format: ChemicalFormatType};
  initialName?: string;
  onMoleculeCreated?: (
    moleculeId: string,
    moleculeName: string,
    parentMolecule?: MoleculeDAO.CreatedParentMolecule
  ) => void;
}

export default function CreateMoleculeModal({
  initialFolderId,
  onRequestClose,
  onRequestSubmit,
  initialChemicalStructure,
  initialName,
  onMoleculeCreated,
}: CreateMoleculeModalProps): JSX.Element {
  // Pre-fill from initialName (e.g. an IUPAC name resolved for an unrecognized molecule); the user
  // can still edit the name before submitting.
  const [name, setName] = useState<string>(initialName ?? '');
  const [folderId, setFolderId] = useState(
    initialFolderId ?? FolderDefaults.get(FolderDefaultsKey.INVENTORY)
  );
  const [sketcherValue, setSketcherValue] = useControlledChemicalStructureDrawer();
  const [posting, setPosting] = useState(false);
  const [studySearchItems, setStudySearchItems] = useState<Array<StudySearchItem>>([]);
  const [tagSchema, tags, schemaSelectorProps, tagInputsProps] = useCreateBioentityTagInputs();

  const counterpartDetection = useCounterpartDetection(
    sketcherValue,
    tagSchema?.id,
    BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS
  );

  const {mismatchResult, pairingMap} = useSchemaMismatchDetection({
    tagSchemaId: BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS ? (tagSchema?.id ?? null) : null,
    hasCounterparts: counterpartDetection.hasCounterparts,
    registryId: tagSchema?.registryId ?? null,
  });

  const isFormSchemaSelected =
    mismatchResult.status === MismatchStatus.OK &&
    mismatchResult.selectedSchemaRole === SelectedSchemaRole.FORM;

  const visibleTagInputsProps = isFormSchemaSelected
    ? {
        ...tagInputsProps,
        tags: tagInputsProps.tags.filter(
          (tag) => tag.tagSchemaField.systemName !== COMPOUND_FIELD_SYSTEM_NAME
        ),
      }
    : tagInputsProps;

  const useNewCreatePath = BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS;

  const getSchemaMetadata = useMemo(() => {
    if (!useNewCreatePath) {
      return undefined;
    }
    return (schema: {id: string}): string | undefined => {
      const pairing = pairingMap[schema.id];
      if (!pairing) {
        return undefined;
      }
      if (pairing.formSchemaId === schema.id) {
        return `Linked to: ${pairing.compoundSchemaName}`;
      }
      return `Linked to: ${pairing.formSchemaName}`;
    };
  }, [useNewCreatePath, pairingMap]);

  const {interfaceIdsBySchemaId, status: ssiQueryStatus} = useSchemaSSIsByIdQuery(!useNewCreatePath);
  const isSsiQueryBlocked =
    useNewCreatePath &&
    (ssiQueryStatus === QueryResultType.PENDING || ssiQueryStatus === QueryResultType.ERROR);
  const tagSchemaFilterFn = useMemo(() => {
    if (!useNewCreatePath || ssiQueryStatus !== QueryResultType.SUCCESS) {
      return undefined;
    }
    return (schema: {id: string}) => {
      const ssiIds = interfaceIdsBySchemaId[schema.id] ?? [];
      return (
        ssiIds.includes(MoleculeSchemaInterfaceApiId.MOLECULE_COMPOUND_ID) ||
        ssiIds.includes(MoleculeSchemaInterfaceApiId.MOLECULE_FORM_ID)
      );
    };
  }, [useNewCreatePath, ssiQueryStatus, interfaceIdsBySchemaId]);

  const disabled =
    name.trim().length === 0 ||
    folderId == null ||
    posting ||
    counterpartDetection.hasBlockingError ||
    counterpartDetection.isParsing ||
    mismatchResult.status === MismatchStatus.BLOCKED ||
    (useNewCreatePath && tagSchema == null);

  async function createMolecule(): Promise<void> {
    assert(folderId != null, 'Unexpected creating molecule with null folderId');
    setPosting(true);

    try {
      if (useNewCreatePath) {
        assert(tagSchema != null, 'Schema is required when salt form bulk create is enabled');
        const schemaFields = buildSchemaFieldsFromTags(tags);
        const molecule = await withLoadingScreen(
          MoleculeDAO.createMoleculeWithParentLink({
            name,
            folderId,
            schemaId: tagSchema.id,
            chemicalStructureInput: {
              structureFormat: ChemicalFormatType.MOLFILE,
              value: sketcherValue ?? EMPTY_MOLFILE,
            },
            ...(Object.keys(schemaFields).length > 0 ? {schemaFields} : {}),
            ...(studySearchItems.length > 0 ? {studyIds: studySearchItems.map(({id}) => id)} : {}),
          }),
          'Creating molecule...'
        );
        writeFragmentsToCache(getFragmentWrapper('Molecule'), [molecule]);
        if (onMoleculeCreated) {
          onMoleculeCreated(molecule.id, molecule.name, molecule.parentMolecule);
        } else {
          FileStates.get().openFolderItem(molecule.id);
        }
      } else {
        const createBioentityArgs: CreateBioentityArgs = {
          ...getDefaultCreateBioentityArgs(),
          name,
          folderId,
          tagSchema,
          tags,
          studySearchItems,
        };
        createBioentityArgs.chemicalSketcherValue = sketcherValue;
        const shouldStripSalts =
          !BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS && sketcherValue != null;
        if (tagSchema != null) {
          await withLoadingScreen(createBioentity(createBioentityArgs), 'Creating molecule...').then(() =>
            shouldStripSalts
              ? stripSaltsAndNotify(ChemicalFormatType.MOLFILE, sketcherValue)
              : Promise.resolve()
          );
        } else {
          await withLoadingScreen(
            MoleculeActions.createMolecule(
              name,
              folderId,
              ChemicalFormatType.MOLFILE,
              sketcherValue ?? EMPTY_MOLFILE,
              null,
              studySearchItems.map(({id}) => id)
            ).then(() =>
              shouldStripSalts
                ? stripSaltsAndNotify(ChemicalFormatType.MOLFILE, sketcherValue)
                : Promise.resolve()
            ),
            'Creating molecule...'
          );
        }
      }
      FolderDAOCache.invalidateFolderContents(folderId);
      onRequestSubmit();
    } catch (err) {
      setPosting(false);
      if (net.isServerRequestError(err)) {
        notifications.notifyError(err);
      } else {
        throw err;
      }
    }
  }

  return (
    <ExpandableModal
      title="Create molecule"
      buttons={[{disabled, submit: true, text: 'Create'}]}
      onRequestClose={onRequestClose}
      onRequestSubmit={createMolecule}
      startMaximized={true}
      analyticsModule={ANALYTICS_MODULE.CHEMISTRY}
      analyticsModalType="create_molecule"
    >
      <Row>
        <Col sm={12}>
          <FormGroup>
            <FieldLabel label="Entity name" isRequired={true} />
            <input
              autoFocus={true}
              type="text"
              value={name}
              placeholder="Name"
              onChange={(e) => {
                setName(e.target.value);
              }}
              {...getTestProps({component: 'CreateMoleculeModal', element: 'input', key: 'entityName'})}
            />
          </FormGroup>
        </Col>
      </Row>
      <Row>
        <Col sm={BenchlingConfig.STUDIES_ENABLE ? 6 : 12}>
          <FormGroup>
            <FieldLabel label={DEFAULT_PROJECT_FOLDER_LABEL_TEXT} isRequired={true} />
            <FolderSelector
              buttonSize={ButtonSize.MEDIUM}
              enableRedesign={true}
              showInventory={true}
              value={folderId}
              onChange={(e) => {
                setFolderId(e.target.value);
              }}
              unsetOnFetchFail={true}
            />
          </FormGroup>
        </Col>
        {BenchlingConfig.STUDIES_ENABLE && (
          <Col sm={6}>
            <FormGroup>
              <FieldLabel label="Studies" />
              <StudySearchInput
                onChange={(studySearchItems: Array<StudySearchItem>) => {
                  setStudySearchItems(studySearchItems);
                }}
                shouldAllowMultiple={true}
                selectedItems={studySearchItems}
              />
            </FormGroup>
          </Col>
        )}
      </Row>
      <Row>
        <Col sm={12}>
          <FormGroup>
            <FieldLabel
              label="Schema"
              isRequired={useNewCreatePath}
              dataTestAttrs={{component: 'CreateMoleculeModal', element: 'schemaLabel'}}
            />
            <TagSchemaSelector
              allowNone={!useNewCreatePath}
              isDisabled={isSsiQueryBlocked}
              oligoAwareFolderItemTypes={[ModelTypeConstants.MOLECULE]}
              tagSchemaFilterFn={tagSchemaFilterFn}
              getSchemaMetadata={getSchemaMetadata}
              {...schemaSelectorProps}
            />
          </FormGroup>
          <FormGroup>
            <CreateBioentityTagInputs {...visibleTagInputsProps} />
          </FormGroup>
        </Col>
      </Row>
      {BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS && (
        <>
          <SchemaMismatchAlert mismatchResult={mismatchResult} />
          <SchemaInfoBanner mismatchResult={mismatchResult} />
        </>
      )}
      <Paper withBorder={BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS} radius="sm" p="md">
        <ChemicalStructureModalInput
          initialSketcherValue={initialChemicalStructure?.value ?? EMPTY_MOLFILE}
          onSketcherValueChange={setSketcherValue}
          mode={ChemicalStructureDrawerMode.BASIC_SEARCH}
          showAttachmentPointBanner={false}
          inputFormat={
            initialChemicalStructure
              ? (CHEMICAL_FORMAT_TO_MARVIN[initialChemicalStructure.format] ?? null)
              : null
          }
          outputFormat={MarvinChemicalFormat.MOLV3}
        />
        {BenchlingConfig.CHEMISTRY_ENABLE_MOLECULE_SALT_FORMS && (
          <>
            <Divider my="lg" size="2px" />
            <ChemicalCounterpartFields
              parseResponse={counterpartDetection.parseResponse}
              isLoading={counterpartDetection.isLoading}
              error={counterpartDetection.error}
              errorCode={counterpartDetection.errorCode}
              entityName={name.trim() || undefined}
            />
          </>
        )}
      </Paper>
    </ExpandableModal>
  );
}

function SchemaInfoBanner({mismatchResult}: {mismatchResult: SchemaMismatchResult}): JSX.Element | null {
  if (mismatchResult.status !== MismatchStatus.OK) {
    return null;
  }

  const {selectedSchemaRole, linkedSchemaName} = mismatchResult;

  if (selectedSchemaRole === SelectedSchemaRole.COMPOUND_WITH_FORM && linkedSchemaName != null) {
    return (
      <Alert
        variant="info"
        mb="sm"
        icon={<Icon icon="info-circle" />}
        styles={{root: {flexShrink: 0}}}
        title="This schema supports the creation of both compound molecules and salted or solvent forms."
      >
        The molecule you draw will be created using this schema or its linked Form schema, {linkedSchemaName},
        if you include any salts or solvents.
      </Alert>
    );
  }

  if (selectedSchemaRole === SelectedSchemaRole.FORM && linkedSchemaName != null) {
    return (
      <Alert
        variant="info"
        mb="sm"
        icon={<Icon icon="info-circle" />}
        styles={{root: {flexShrink: 0}}}
        title="This schema supports the creation of both compound molecules and salted or solvent forms."
      >
        The molecule you draw will be created using this schema or its linked Compound schema,{' '}
        {linkedSchemaName}, if you do not include any salts or solvents.
      </Alert>
    );
  }

  return null;
}

function buildSchemaFieldsFromTags(tags: Array<SchemaTag>): Record<string, unknown> {
  const result: Record<string, unknown> = {};
  for (const tag of tags) {
    const key = relevantKeyFromFieldType(tag.tagSchemaField.fieldType);
    if (key) {
      const value = tag[key];
      if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) {
        continue;
      }
      // Handle link fields (bioentity, batch, entry, etc.) - map objects to IDs
      if (getLinksKeyFromFieldType(tag.tagSchemaField.fieldType) != null && Array.isArray(value)) {
        result[tag.tagSchemaField.systemName] = value.map((item: {id: string}) => item.id);
      } else if (key === 'schemaFieldSelectorOptionIds') {
        // Handle selector/dropdown fields
        // For single-select, the backend expects a single string ID, not an array
        // For multi-select, the backend expects an array of string IDs
        // The frontend always stores schemaFieldSelectorOptionIds as an array
        if (Array.isArray(value)) {
          result[tag.tagSchemaField.systemName] = tag.tagSchemaField.isMulti ? value : value[0];
        } else {
          result[tag.tagSchemaField.systemName] = value;
        }
      } else {
        result[tag.tagSchemaField.systemName] = value;
      }
    }
  }
  return result;
}
